/**
 * SASADM-0138 — per-product GHL Marketplace app registration + installs
 * (saas-auth SASATH-0154 / SASATH-0288).
 *
 * Mirrors `internalKeys.ts`: product-scoped, SUPER_ADMIN-only, strict
 * `request()` + `unwrap()` under the standard `ApiResponse<T>` envelope.
 *
 * 404 is the NORMAL "not configured yet" answer on `get` (the service throws
 * `ResourceNotFoundException` when no row exists) and the NORMAL answer on
 * `listInstalls` until SASATH-0288 ships — both resolve to an empty state
 * rather than throwing. Every other `ApiError` propagates.
 */
import { request, unwrap } from '../client';
import { ApiError } from '../errors';
import type {
  ApiResponse,
  GhlMarketplaceAppDto,
  GhlMarketplaceInstallDto,
  UpsertGhlMarketplaceAppRequest,
} from '../types';

const base = (productId: number) => `/api/saas-admin/products/${productId}/ghl-marketplace-app`;

export const ghlMarketplaceAppApi = {
  async get(productId: number): Promise<GhlMarketplaceAppDto | null> {
    try {
      const res = await request<ApiResponse<GhlMarketplaceAppDto>>('GET', base(productId));
      return unwrap(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  async upsert(
    productId: number,
    req: UpsertGhlMarketplaceAppRequest,
  ): Promise<GhlMarketplaceAppDto> {
    const res = await request<ApiResponse<GhlMarketplaceAppDto>>('PUT', base(productId), {
      json: req,
    });
    return unwrap(res);
  },

  async delete(productId: number): Promise<void> {
    await request<void>('DELETE', base(productId));
  },

  // SASATH-0288 pending: until it ships this 404s. Treat 404 as an empty
  // list — permanently correct, since after 0288 ships it answers 200 [] and
  // this branch simply stops being exercised.
  async listInstalls(productId: number): Promise<GhlMarketplaceInstallDto[]> {
    try {
      const res = await request<ApiResponse<GhlMarketplaceInstallDto[]>>(
        'GET',
        `/api/saas-admin/products/${productId}/ghl-marketplace-installs`,
      );
      return unwrap(res) ?? [];
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return [];
      throw err;
    }
  },
};
